import { ToggleButton, ToggleButtonGroup, FormHelperText, Box } from '@mui/material';
import { useInput, useTranslate, InputProps } from 'react-admin';

interface ButtonGroupInputProps extends Omit<InputProps, 'source'> {
  source: string;
  choices: { id: string; name: string }[];
  label?: string;
  helperText?: string;
  onValueChange?: (value: string) => void;
}

/**
 * 药丸样式的单选按钮组（ToggleButtonGroup），用于「来源 / 级别」等枚举选择。
 * 通过 react-admin 的 useInput 接入表单，行为与 RadioButtonGroupInput 一致但视觉为按钮。
 */
export const ButtonGroupInput = ({
  source,
  choices,
  label,
  helperText,
  onValueChange,
  defaultValue,
  validate,
  ...rest
}: ButtonGroupInputProps) => {
  const translate = useTranslate();
  const {
    field,
    fieldState: { error, invalid },
    isRequired,
  } = useInput({ source, defaultValue, validate, ...rest });

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={field.value ?? null}
        onChange={(_e, value) => {
          if (value === null) return; // 不允许取消选择
          field.onChange(value);
          onValueChange?.(value);
        }}
        sx={{
          '& .MuiToggleButton-root': {
            px: 2.5,
            borderRadius: 1,
            textTransform: 'none',
            border: '1px solid',
            borderColor: 'divider',
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          },
        }}
      >
        {choices.map((choice) => (
          <ToggleButton key={choice.id} value={choice.id}>
            {translate(choice.name, { _: choice.name })}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {(invalid || helperText) && (
        <FormHelperText error={invalid}>
          {invalid ? error?.message : helperText}
          {isRequired && !invalid ? ' *' : ''}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ButtonGroupInput;
